import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import ReactSVG from 'react-svg';
import { PersonAdd, Edit, Textsms } from '@material-ui/icons';
import { Typography } from '@material-ui/core';
import * as lib from '~/dependencies';
import {
  isLocalExtension,
  formatInternationalNumber,
  formatInternationalE164,
  isExtension,
  isNotAnonymous,
  isMobileNumber,
  isUSNumber,
} from '~/helpers';
import { useTryToCall, useNavigation } from '~/hooks';
import MovablePreference from '~/components/Integrations/MovablePreference';
import { IconTag, IconNote } from '~/assets/svgIcons';

import {
  Wrapper,
  Container,
  CallItem,
  StatusText,
  DetailsContainer,
  ActionsContainer,
  CallBackButton,
} from './styles';

export default function TerminatedCall(props) {
  // PROPS
  const { match, displayName, numberFrom, contactId, callInformations } = props;
  // REFs
  const btnCallback = useRef();

  // STATES
  const [enableCallback, setEnableCallback] = useState(true);

  // HOOKS
  const tryToCall = useTryToCall();
  const navigate = useNavigation();
  const { t } = lib.useTranslation();

  // SELECTORS
  const userCountry = useSelector(state => state.auth.userCountry);

  const isIntegrated = useSelector(state => state.integrations.isIntegrated);

  const hasSMSCapability =
    isMobileNumber(numberFrom, userCountry) || isUSNumber(numberFrom);

  /**
   *  Call-ID: 1ebfef2a2ddb07f64181d558567a2805@secure-call.nuacom.net
   * @returns  1ebfef2a2ddb07f64181d558567a2805
   */
  function getCallIdFormatted() {
    if (callInformations?.session) {
      const xCallIdFormatted = String(
        callInformations?.session.request.getHeader('Call-ID')
      )
        .split('@')[0]
        .replace('"', '');

      return xCallIdFormatted;
    }

    return '';
  }

  function handleBtn(type) {
    const { setAddTags, setAddCallNotes, addTags, addCallNotes } = props;
    setTimeout(() => {
      if (type === 'tag') {
        setAddTags(!addTags);
      } else {
        setAddCallNotes(!addCallNotes);
      }
    }, 1000);
  }

  useEffect(() => {
    setTimeout(() => {
      setEnableCallback(false);
    }, 2000);
  }, []);

  function handleTryToCall() {
    if (!enableCallback) {
      btnCallback.current.disabled = true;
      if (displayName) {
        tryToCall(numberFrom, { name: displayName });
      } else {
        tryToCall(numberFrom);
      }
    }
  }

  // NOTE: checkAndStoreMandatoryCallTagging is now dispatched from callCurrent.ts
  // in the SessionState.Terminated handler. This ensures ACW appears regardless
  // of which screen the call was initiated from (Dialpad, Call Details, Contacts, etc.).

  function handleClickSMSButton() {
    navigate.smsSenderView({
      ...(callInformations?.contactId
        ? { id: callInformations.contactId }
        : {}),
      name: callInformations?.contactName || callInformations.numberFrom,
      mobile: callInformations.numberFrom,
    });
  }

  return (
    <>
      <Wrapper onDoubleClick={() => false}>
        <Container>
          <CallItem>
            <DetailsContainer>
              <div className="left">
                <div>
                  <Typography variant="subtitle1" align="left">
                    <strong>{formatInternationalE164(displayName)}</strong>
                  </Typography>
                </div>
                <div className="informations">
                  <Typography variant="subtitle2" align="left">
                    <strong>{formatInternationalNumber(numberFrom)}</strong>
                  </Typography>
                </div>
              </div>
              <div className="right">
                <StatusText>
                  {t('screens.displayCall.terminatedCall.callFinished')}
                </StatusText>
              </div>
            </DetailsContainer>
            <ActionsContainer>
              <div className="actions-buttons">
                <button
                  type="button"
                  title={t('screens.displayCall.terminatedCall.tags')}
                  onClick={() => handleBtn('tag')}>
                  <ReactSVG src={IconTag} className="icon-btn" />
                </button>
                <button
                  type="button"
                  title={t('screens.displayCall.terminatedCall.notes')}
                  onClick={() => handleBtn('note')}>
                  <ReactSVG src={IconNote} className="icon-btn" />
                </button>
                <div className="dtmfBox">
                  {!isLocalExtension(numberFrom) && (
                    <lib.Link
                      aria-label={
                        contactId !== null
                          ? t('screens.displayCall.terminatedCall.editContact')
                          : t('screens.displayCall.terminatedCall.addContact')
                      }
                      to={{
                        pathname: `${match.path}/addNewContact`,
                        state: {
                          ...(contactId == null && {
                            landLine: numberFrom,
                          }),
                          ...(contactId !== null && {
                            id: contactId,
                            edit: true,
                          }),
                          prevScreen: 'TerminatedCall',
                        },
                        ...(contactId !== null && {
                          hash: '#edit',
                        }),
                      }}>
                      <button
                        type="button"
                        title={
                          contactId !== null
                            ? t(
                                'screens.displayCall.terminatedCall.editContact'
                              )
                            : t('screens.displayCall.terminatedCall.addContact')
                        }
                        aria-label={t(
                          'screens.displayCall.terminatedCall.addContact'
                        )}>
                        {contactId ? (
                          <Edit className="icon-btn" />
                        ) : (
                          <PersonAdd className="icon-btn" />
                        )}
                      </button>
                    </lib.Link>
                  )}
                </div>

                {hasSMSCapability && (
                  <button
                    type="button"
                    title="SMS"
                    data-test-id="btn-sms-open"
                    onClick={handleClickSMSButton}>
                    <Textsms className="icon-btn" />
                  </button>
                )}
              </div>
              <CallBackButton
                ref={btnCallback}
                onClick={handleTryToCall}
                className="green"
                type="button"
                finished={enableCallback}
                disabled={btnCallback.current?.disabled}>
                {t('screens.displayCall.terminatedCall.callBack')}
              </CallBackButton>
            </ActionsContainer>

            {!isExtension(numberFrom) &&
              isNotAnonymous(numberFrom) &&
              isIntegrated && (
                <MovablePreference callId={getCallIdFormatted()} />
              )}
          </CallItem>
        </Container>
      </Wrapper>
    </>
  );
}

TerminatedCall.defaultProps = {
  match: [],
  displayName: '',
  numberFrom: '',
  setAddTags: () => {},
  setAddCallNotes: () => {},
  addTags: false,
  addCallNotes: false,
  contactId: null,
  callInformations: [],
};

TerminatedCall.propTypes = {
  match: lib.PropTypes.instanceOf(Object),
  displayName: lib.PropTypes.string,
  numberFrom: lib.PropTypes.string,
  setAddTags: lib.PropTypes.func,
  setAddCallNotes: lib.PropTypes.func,
  addTags: lib.PropTypes.bool,
  addCallNotes: lib.PropTypes.bool,
  contactId: lib.PropTypes.number,
  callInformations: lib.PropTypes.instanceOf(Object),
};
